import { Box, Button, CloseButton, Group, Image, Text } from "@mantine/core";
import { visualsFor } from "./categories";
import { announcementOpenPath } from "./helpers";
import { sentAtNote } from "./time";
import type { PortalAnnouncement } from "./types";

/** Height of the image strip. Taller than the ~44px text line because a
 *  poster is unreadable at text-line height; shorter on phones so the strip
 *  does not eat the first screen. The breakpoint is a plain 600px rather than
 *  a Mantine one (`sm` is 768px) — it tracks phone width, not the layout grid. */
const IMAGE_STRIP_CSS =
  ".nodus-ann-banner-image{height:180px}" +
  "@media (max-width:600px){.nodus-ann-banner-image{height:120px}}";

/** Top banner for a single active announcement. Styled by category;
 *  the wording follows the operator's title/message (never fabricated).
 *  Multiple banners are stacked by the caller.
 *
 *  With an ``image`` the banner is an image-only strip instead: the poster
 *  fills a taller strip with the operator's ``object_fit`` /
 *  ``object_position``, and the title (or category label) moves into the alt
 *  text. A poster already carries its own wording, so drawing the title over
 *  it would fight the design — the same reasoning as the corner/modal heroes.
 *
 *  ``onOpen`` + the announcement's ``portal_path`` add an "Open" button that
 *  hands the path to the host (the portal navigates there, e.g. to a guide). */
export function AnnouncementBanner({
  announcement,
  onDismiss,
  onOpen,
}: {
  announcement: PortalAnnouncement;
  onDismiss?: () => void;
  onOpen?: (path: string) => void;
}) {
  // Colour comes from the CATEGORY, never severity (#327) — a drill must not
  // paint itself the same red as a real evacuation. Severity still decides
  // whether the strip pulses.
  const visuals = visualsFor(announcement.category);
  const pulse = visuals.pulseLed || announcement.severity === "critical";
  const label = announcement.title?.trim() || visuals.label;
  const body = announcement.message?.trim() || "";
  const image = announcement.image;
  const openPath = onOpen ? announcementOpenPath(announcement) : null;
  const openButton = openPath ? (
    <Button
      size="compact-sm"
      variant="white"
      color="dark"
      onClick={() => onOpen?.(openPath)}
      aria-label={`Open: ${label}`}
    >
      Open
    </Button>
  ) : null;

  if (image?.url) {
    return (
      <Box
        className="nodus-ann-banner-image"
        pos="relative"
        style={{
          // The letterbox around a ``contain`` fit shows the category colour,
          // so the strip still reads as this category edge to edge.
          background: visuals.background,
          // No opacity pulse over a poster — it would flicker the artwork.
          // The category border carries the signal instead, heavier when the
          // text banner would have pulsed.
          border: `${pulse ? 4 : 2}px solid ${visuals.background}`,
          overflow: "hidden",
        }}
      >
        <Image
          src={image.url}
          alt={label}
          h="100%"
          w="100%"
          fit={image.object_fit ?? "cover"}
          style={{ objectPosition: image.object_position || "center" }}
        />
        {openButton && (
          <Box pos="absolute" style={{ right: 8, bottom: 8, zIndex: 1 }}>
            {openButton}
          </Box>
        )}
        {onDismiss && (
          <CloseButton
            aria-label="Dismiss announcement"
            onClick={onDismiss}
            c="white"
            variant="transparent"
            radius="xl"
            pos="absolute"
            // The button can land on any colour of the poster, so it carries
            // its own dark circular scrim.
            style={{ right: 8, top: 8, zIndex: 1, background: "rgba(0,0,0,0.55)" }}
          />
        )}
        <style>{IMAGE_STRIP_CSS}</style>
      </Box>
    );
  }

  return (
    <Box
      c="white"
      ta="center"
      fw={600}
      py={10}
      px={40}
      pos="relative"
      style={{
        background: visuals.background,
        ...(pulse ? { animation: "nodus-ann-pulse 1.5s ease-in-out infinite" } : {}),
      }}
    >
      <Group gap="sm" justify="center" wrap="wrap">
        <Text component="span" fw={600} c="white">
          {visuals.icon} {label}
          {announcement.is_site_wide ? " — SITE-WIDE" : ""}
          {body ? `: ${body}` : ""}
        </Text>
        {openButton}
      </Group>
      {sentAtNote(announcement.raised_at) && (
        <Text component="div" fz="xs" c="white" ta="center" style={{ opacity: 0.85, fontWeight: 400 }}>
          {sentAtNote(announcement.raised_at)}
        </Text>
      )}
      {onDismiss && (
        <CloseButton
          aria-label="Dismiss announcement"
          onClick={onDismiss}
          c="white"
          variant="transparent"
          pos="absolute"
          style={{ right: 8, top: "50%", transform: "translateY(-50%)" }}
        />
      )}
      <style>{`@keyframes nodus-ann-pulse{0%,100%{opacity:1}50%{opacity:.85}}`}</style>
    </Box>
  );
}
